import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Paper,
  Group,
  Stack,
  Badge,
  Avatar,
  ThemeIcon,
  Button,
  ActionIcon,
  Grid,
  Loader,
  Center,
  RingProgress,
  Modal,
  Select,
  Alert,
  Progress,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { AreaChart, BarChart } from "@mantine/charts";
import {
  IconUsers,
  IconActivity,
  IconWallet,
  IconRefresh,
  IconDotsVertical,
  IconTrophy,
  IconCheck,
  IconDownload,
  IconFileSpreadsheet,
  IconAlertTriangle,
  IconClipboardCheck,
  IconCoin,
  IconUserCheck,
} from "@tabler/icons-react";
import axios from "axios";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../hooks/usePerformRequest";
import { virelCore } from "../../../lib/data";

export default function Overview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [timeRange, setTimeRange] = useState<string | null>("all");
  const [customDates, setCustomDates] = useState<[Date | null, Date | null]>([null, null]);
  const [reportType, setReportType] = useState<string | null>("users");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await PerformRequest(Endpoints.GetDashboardStats());
      if (res.status === 200 || res.status === 201) {
        setData((res.data as any).data);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    document.title = "Overview | Virel Admin";
  }, []);

  const isExportValid = timeRange && reportType && (timeRange !== "custom" || (customDates[0] && customDates[1]));

  const handleExport = async () => {
    if (!reportType || !timeRange) return;

    setExportLoading(true);
    try {
      const now = new Date();
      let startDate: string | undefined;
      let endDate: string | undefined;

      switch (timeRange) {
        case "7d":
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case "30d":
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case "custom":
          if (customDates[0]) {
            const start = new Date(customDates[0]);
            startDate = start.toISOString();
          }
          if (customDates[1]) {
            const end = new Date(customDates[1]);
            end.setHours(23, 59, 59, 999);
            endDate = end.toISOString();
          }
          break;
        case "all":
        default:
          startDate = undefined;
          endDate = undefined;
          break;
      }

      if (timeRange === "custom" && (!startDate || !endDate)) {
        console.error("Custom date range requires both start and end dates");
        setExportLoading(false);
        return;
      }

      const token = localStorage.getItem("t");
      let endpoint = "";
      let filename = "";

      switch (reportType) {
        case "withdrawals":
          endpoint = Endpoints.ExportWithdrawals({ startDate, endDate }).route;
          filename = `withdrawals_${new Date().toISOString()}.csv`;
          break;
        case "missions":
          endpoint = Endpoints.ExportMissions({ startDate, endDate }).route;
          filename = `missions_${new Date().toISOString()}.csv`;
          break;
        case "campaigns":
          endpoint = Endpoints.ExportCampaigns({ startDate, endDate }).route;
          filename = `campaigns_${new Date().toISOString()}.csv`;
          break;
        case "users":
        default:
          endpoint = Endpoints.ExportUsers({ startDate, endDate }).route;
          filename = `users_${new Date().toISOString()}.csv`;
          break;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setReportModalOpen(false);
    } catch (error) {
      console.error("Error exporting report:", error);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="xl" type="dots" />
      </Center>
    );
  }

  // Fallback if data is missing (should not happen with new backend)
  const users = data?.users || {};
  const finance = data?.finance || {};
  const activities = data?.activities || {};
  const proofs = data?.proofs || {};
  const userGrowth = users.growthChart || [];
  const revenueChart = finance.volumeChart || [];
  const leaderboard = data?.leaderboard || [];
  const recentProofs = proofs.recent || [];
  const pilot = data?.pilot; // present once the API ships the pilot section
  const naira = (n: number) => `₦${(n || 0).toLocaleString()}`;

  return (
    <Container size="xl" py="lg">
      {/* Header */}
      <Group justify="space-between" align="center" mb="xl">
        <div>
          <Title order={2} fw={800}>
            Overview
          </Title>
          <Text c="dimmed" size="sm">
            Welcome back, here's what's happening today.
          </Text>
        </div>
        <Group>
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={fetchData}
            loading={loading}
          >
            Refresh
          </Button>
          <Button variant="filled" color="dark" onClick={() => setReportModalOpen(true)}>
            Download Report
          </Button>
        </Group>
      </Group>

      {/* Key Stats */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="xl">
        <StatCard
          title="Total Users"
          value={users.total?.toLocaleString() || "0"}
          diff={users.newThisMonth}
          diffLabel="new this month"
          icon={IconUsers}
          color="brand"
        />
        <StatCard
          title="Total Withdrawn"
          value={`₦${(finance.totalVolume || 0).toLocaleString()}`}
          subtitle={`${finance.processedRequests || 0} withdrawal payouts processed all-time`}
          icon={IconWallet}
          color="teal"
        />
        <StatCard
          title="Active Activities"
          value={activities.active?.toLocaleString() || "0"}
          diff={activities.total}
          diffLabel="total activities"
          icon={IconActivity}
          color="grape"
          trend="neutral"
        />
        <StatCard
          title="Pending Proofs"
          value={proofs.pending?.toLocaleString() || "0"}
          subtitle={`${(proofs.totalApproved || 0).toLocaleString()} approved all-time`}
          icon={IconCheck}
          color="orange"
          onClick={() => navigate("/d/proofs")}
        />
      </SimpleGrid>

      {/* ── Today (pilot analytics) ─────────────────────────────── */}
      {pilot && (
        <>
          {!pilot.integrity?.ok && (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              title="Completion counters out of sync"
              mb="md"
            >
              <Text size="sm">
                {pilot.integrity.driftedMissions.length} opportunit
                {pilot.integrity.driftedMissions.length === 1 ? "y" : "ies"} show a
                completion count that doesn't match approved proofs — capacity may
                be blocked by phantom slots. Run{" "}
                <Text span ff="monospace" size="sm">
                  fixCompletionCounts.ts
                </Text>{" "}
                on the API.
              </Text>
              <Stack gap={2} mt="xs">
                {pilot.integrity.driftedMissions.slice(0, 5).map((m: any) => (
                  <Text size="xs" c="dimmed" key={m.title}>
                    {m.title}: shows {m.stored}, really {m.actual}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Group justify="space-between" align="center" mb="sm">
            <Title order={3}>Today</Title>
            <Badge variant="light" color="brand">
              {pilot.date}
            </Badge>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="md">
            <StatCard
              title="Opportunities Live Today"
              value={String(pilot.board.liveCount)}
              subtitle={`a user who does them all earns ${naira(pilot.board.perUserValue)}`}
              icon={IconActivity}
              color="brand"
              onClick={() => navigate("/d/analytics/board")}
            />
            <StatCard
              title="Paid Out Today"
              value={naira(pilot.money.paidMissionToday)}
              progress={
                pilot.money.budgetCapToday > 0
                  ? (pilot.money.paidMissionToday / pilot.money.budgetCapToday) * 100
                  : 0
              }
              progressColor="teal"
              subtitle={`${
                pilot.money.budgetCapToday > 0
                  ? Math.round(
                      (pilot.money.paidMissionToday / pilot.money.budgetCapToday) * 100,
                    )
                  : 0
              }% of today's ${naira(pilot.money.budgetCapToday)} cap${
                pilot.money.paidOtherToday > 0
                  ? ` · plus ${naira(pilot.money.paidOtherToday)} bonuses`
                  : ""
              }`}
              icon={IconCoin}
              color="teal"
              onClick={() => navigate("/d/analytics/money")}
            />
            <StatCard
              title="Proofs Today"
              value={String(
                (pilot.proofs.today.approved || 0) +
                  (pilot.proofs.today.pending || 0) +
                  (pilot.proofs.today.rejected || 0),
              )}
              subtitle={`${pilot.proofs.today.approved || 0} approved · ${
                pilot.proofs.today.rejected || 0
              } rejected · ${pilot.proofs.pendingQueue} waiting for review`}
              icon={IconClipboardCheck}
              color="grape"
              onClick={() => navigate("/d/analytics/proofs")}
            />
            <StatCard
              title="Withdrawals"
              value={naira(pilot.money.pendingWithdrawals.amount)}
              subtitle={`${
                pilot.money.pendingWithdrawals.count
              } awaiting approval · ${pilot.money.withdrawalsToday.count} requested today (${naira(
                pilot.money.withdrawalsToday.amount,
              )})`}
              icon={IconWallet}
              color="orange"
              onClick={() => navigate("/d/analytics/withdrawals")}
            />
          </SimpleGrid>

          {/* Pilot budget strip */}
          {pilot.money.pilotBudget && (
            <Paper
              p="md"
              radius="md"
              withBorder
              mb="md"
              onClick={() => navigate("/d/analytics/budget")}
              style={{ cursor: "pointer" }}
            >
              <Group justify="space-between" mb={6}>
                <Text size="sm" fw={600}>
                  Pilot Campaign Budget
                </Text>
                <Text size="sm" c="dimmed">
                  {naira(pilot.money.pilotBudget.spent)} used of{" "}
                  {naira(pilot.money.pilotBudget.total)} (
                  {Math.round(
                    (pilot.money.pilotBudget.spent / pilot.money.pilotBudget.total) * 100,
                  )}
                  %)
                </Text>
              </Group>
              <Progress
                value={Math.min(
                  100,
                  (pilot.money.pilotBudget.spent / pilot.money.pilotBudget.total) * 100,
                )}
                color={
                  pilot.money.pilotBudget.spent > pilot.money.pilotBudget.total
                    ? "red"
                    : "brand.7"
                }
                size="md"
                radius="xl"
                aria-label={`Pilot budget: ${naira(pilot.money.pilotBudget.spent)} of ${naira(pilot.money.pilotBudget.total)}`}
              />
              <Text size="xs" c="dimmed" mt={6}>
                Everything paid for completed Opportunities since the pilot started on
                July 13.
              </Text>
            </Paper>
          )}

          <Grid gutter="md" mb="md">
            {/* Today's board with fill rates */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Paper p="md" radius="md" withBorder h="100%">
                <Group justify="space-between" mb="md">
                  <Title order={4}>Today's Board</Title>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => navigate("/d/analytics/board")}
                  >
                    More details
                  </Button>
                </Group>
                <Stack gap="sm">
                  {pilot.board.missions.length === 0 ? (
                    <Text c="dimmed" ta="center" py="xl">
                      No opportunities scheduled for today
                    </Text>
                  ) : (
                    pilot.board.missions.map((m: any) => {
                      const pct = m.target > 0 ? (m.completions / m.target) * 100 : 0;
                      const full = m.target > 0 && m.completions >= m.target;
                      return (
                        <div key={m.id}>
                          <Group justify="space-between" mb={4} wrap="nowrap">
                            <Text size="sm" fw={500} truncate style={{ flex: 1 }}>
                              {m.title}
                            </Text>
                            <Group gap="xs" wrap="nowrap">
                              <Text size="xs" c="dimmed">
                                {m.completions}/{m.target} · ₦{m.points}
                              </Text>
                              {full ? (
                                <Badge size="xs" color="orange" variant="light">
                                  FULL
                                </Badge>
                              ) : m.daysLeft !== null ? (
                                <Badge size="xs" color="gray" variant="light">
                                  {m.daysLeft === 0 ? "ends today" : `${m.daysLeft}d left`}
                                </Badge>
                              ) : null}
                            </Group>
                          </Group>
                          <Progress
                            value={pct}
                            color="brand.7"
                            size="sm"
                            radius="xl"
                            aria-label={`${m.title}: ${m.completions} of ${m.target} completions`}
                          />
                        </div>
                      );
                    })
                  )}
                </Stack>
              </Paper>
            </Grid.Col>

            {/* 7-day real activity */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Paper p="md" radius="md" withBorder h="100%">
                <Group justify="space-between" mb="md">
                  <Title order={4}>Activity — Last 7 Days</Title>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => navigate("/d/analytics/users")}
                  >
                    More details
                  </Button>
                </Group>
                <BarChart
                  h={260}
                  data={pilot.activity.week.map((d: any) => ({
                    ...d,
                    day: d.date.slice(5), // MM-DD
                  }))}
                  dataKey="day"
                  series={[
                    { name: "active", label: "Active users", color: "#26a642" },
                    { name: "signups", label: "New signups", color: "#339af0" },
                  ]}
                  withLegend
                  tickLine="none"
                  gridAxis="y"
                  withYAxis={false}
                />
              </Paper>
            </Grid.Col>
          </Grid>

          <Grid gutter="md" mb="xl">
            {/* Rejection feed */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Paper p="md" radius="md" withBorder h="100%">
                <Group justify="space-between" mb="md">
                  <Group gap="xs">
                    <Title order={4}>Recent Rejections</Title>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => navigate("/d/analytics/proofs")}
                    >
                      More details
                    </Button>
                  </Group>
                  {pilot.proofs.approvalRate7d !== null && (
                    <Badge
                      variant="light"
                      color={pilot.proofs.approvalRate7d >= 95 ? "brand" : "orange"}
                    >
                      {pilot.proofs.approvalRate7d}% approved (7d)
                    </Badge>
                  )}
                </Group>
                <Stack gap="sm">
                  {pilot.proofs.recentRejections.length === 0 ? (
                    <Text c="dimmed" ta="center" py="xl">
                      No rejections in the last 7 days
                    </Text>
                  ) : (
                    pilot.proofs.recentRejections.map((r: any, i: number) => (
                      <div key={i}>
                        <Group justify="space-between" wrap="nowrap">
                          <Text size="sm" fw={500} truncate>
                            {r.mission}
                          </Text>
                          <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                            {new Date(r.date).toLocaleDateString()}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {r.reason}
                        </Text>
                      </div>
                    ))
                  )}
                </Stack>
              </Paper>
            </Grid.Col>

            {/* Monetization funnel + day-by-day boards */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Stack gap="md" h="100%">
              {pilot.boardsSummary && pilot.boardsSummary.length > 0 && (
                <Paper p="md" radius="md" withBorder>
                  <Group justify="space-between" mb="md">
                    <Title order={4}>Boards Day by Day</Title>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => navigate("/d/analytics/boards")}
                    >
                      More details
                    </Button>
                  </Group>
                  <Stack gap="xs">
                    {[...pilot.boardsSummary]
                      .reverse()
                      .slice(0, 6)
                      .map((b: any) => (
                      <Group key={b.date} justify="space-between" wrap="nowrap">
                        <div>
                          <Group gap="xs">
                            <Text size="sm" fw={600}>
                              {new Date(`${b.date}T12:00:00`).toLocaleDateString("en-GB", {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                              })}
                            </Text>
                            <Badge
                              size="xs"
                              variant="light"
                              color={
                                b.status === "open"
                                  ? "brand"
                                  : b.status === "scheduled"
                                    ? "blue"
                                    : "gray"
                              }
                            >
                              {b.status === "open"
                                ? "OPEN"
                                : b.status === "scheduled"
                                  ? "SCHEDULED"
                                  : "ENDED"}
                            </Badge>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {b.missionCount} Opportunities · {naira(b.perUserValue)} per
                            user
                          </Text>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <Text size="sm" fw={600}>
                            {b.completions.toLocaleString()}
                          </Text>
                          <Text size="xs" c="dimmed">
                            done · {naira(b.valueDelivered)}
                          </Text>
                        </div>
                      </Group>
                    ))}
                    {pilot.boardsSummary.length > 6 && (
                      <Text
                        size="xs"
                        c="dimmed"
                        ta="center"
                        mt={2}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/d/analytics/boards")}
                      >
                        + {pilot.boardsSummary.length - 6} earlier day
                        {pilot.boardsSummary.length - 6 === 1 ? "" : "s"} — view all
                      </Text>
                    )}
                  </Stack>
                </Paper>
              )}
              <Paper
                p="md"
                radius="md"
                withBorder
                style={{ flex: 1, cursor: "pointer" }}
                onClick={() => navigate("/d/funnel")}
              >
                <Group justify="space-between" mb="md">
                  <Title order={4}>Monetization Funnel</Title>
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => navigate("/d/funnel")}
                  >
                    Full breakdown
                  </Button>
                </Group>
                <Group justify="center" gap="xl">
                  <RingProgress
                    size={140}
                    thickness={14}
                    roundCaps
                    sections={[
                      {
                        value:
                          pilot.funnel.totalUsers > 0
                            ? (pilot.funnel.monetized / pilot.funnel.totalUsers) * 100
                            : 0,
                        color: "brand.7",
                      },
                    ]}
                    label={
                      <Text ta="center" fw={700} size="lg">
                        {pilot.funnel.totalUsers > 0
                          ? Math.round(
                              (pilot.funnel.monetized / pilot.funnel.totalUsers) * 100,
                            )
                          : 0}
                        %
                      </Text>
                    }
                  />
                  <Stack gap="xs">
                    <Group gap="xs">
                      <ThemeIcon color="brand" variant="light" size="sm">
                        <IconUserCheck size={14} />
                      </ThemeIcon>
                      <Text size="sm">
                        <Text span fw={700}>
                          {pilot.funnel.monetized}
                        </Text>{" "}
                        monetized
                      </Text>
                    </Group>
                    <Group gap="xs">
                      <ThemeIcon color="gray" variant="light" size="sm">
                        <IconUsers size={14} />
                      </ThemeIcon>
                      <Text size="sm">
                        <Text span fw={700}>
                          {pilot.funnel.stillInTutorial}
                        </Text>{" "}
                        not monetized
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      of {pilot.funnel.totalUsers} total users
                    </Text>
                  </Stack>
                </Group>
                <Text size="xs" c="brand.6" ta="center" mt="md" fw={500}>
                  Click for the full stage-by-stage drop-off — and message members
                  by WhatsApp →
                </Text>
              </Paper>
              </Stack>
            </Grid.Col>
          </Grid>
        </>
      )}

      <Grid gutter="md" mb="xl">
        {/* Main Chart: User Growth */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper p="md" radius="md" withBorder h="100%">
            <Group justify="space-between" mb="md">
              <Title order={4}>User Growth</Title>
              <Badge variant="light" color="brand">
                Last 30 Days
              </Badge>
            </Group>
            <AreaChart
              h={300}
              data={userGrowth}
              dataKey="date"
              series={[{ name: "count", color: "brand.6" }]}
              curveType="monotone"
              gridAxis="xy"
              tickLine="none"
              withYAxis={false}
              tooltipAnimationDuration={200}
            />
          </Paper>
        </Grid.Col>

        {/* Secondary Chart: Revenue */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper p="md" radius="md" withBorder h="100%">
            <Group justify="space-between" mb="md">
              <Title order={4}>Daily Payouts</Title>
              <MenuButton />
            </Group>
            <BarChart
              h={300}
              data={revenueChart}
              dataKey="date"
              series={[{ name: "amount", color: "teal.6" }]}
              tickLine="none"
              gridAxis="y"
              withYAxis={false}
            />
          </Paper>
        </Grid.Col>
      </Grid>

      <Grid gutter="md">
        {/* Recent Activity Feed */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper p="md" radius="md" withBorder h="100%">
            <Title order={4} mb="md">
              Recent Activity
            </Title>
            <Stack gap="sm">
              {recentProofs.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No recent activity
                </Text>
              ) : (
                recentProofs.map((proof: any) => (
                  <Group key={proof.id} justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar radius="xl" size="md" color="blue">
                        {proof.user?.firstName?.[0]}
                      </Avatar>
                      <div style={{ maxWidth: 200 }}>
                        <Text size="sm" fw={500} truncate>
                          {proof.user?.firstName} {proof.user?.lastName}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {proof.status === "pending" ? "Submitted" : "Completed"}: {proof.mission?.title}
                        </Text>
                        <Badge size="xs" variant="light" color="yellow" mt={4}>
                          Level {proof.user?.level ?? 1}
                        </Badge>
                      </div>
                    </Group>
                    <div style={{ textAlign: "right" }}>
                      <Text 
                        size="sm" 
                        fw={500} 
                        c={proof.status === "pending" ? "blue" : "green"}
                      >
                        {proof.status === "pending" ? "Pending" : `+${proof.mission?.points} Coins`}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {new Date(proof.date).toLocaleDateString()}
                      </Text>
                    </div>
                  </Group>
                ))
              )}
            </Stack>
          </Paper>
        </Grid.Col>

        {/* Leaderboard */}
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper p="md" radius="md" withBorder h="100%" bg="dark.8">
            <Group justify="space-between" mb="lg">
              <Group gap="xs">
                <IconTrophy size={20} color="gold" />
                <Title order={4} c="white">
                  Leaderboard
                </Title>
              </Group>
              <Badge color="yellow" variant="light">
                Top 5
              </Badge>
            </Group>
            <Stack gap="md">
              {leaderboard.map((user: any, index: number) => {
                const isVirelCore = user.isVirelCore || (user.email && virelCore.includes(user.email));
                return (
                  <Group key={user.id} justify="space-between">
                    <Group gap="sm">
                      <Text fw={700} c={isVirelCore ? "red.5" : "dimmed"} w={20} ta="center">
                        {index + 1}
                      </Text>
                      <Avatar radius="xl" size="sm" color={isVirelCore ? "red" : undefined} />
                      <Text c={isVirelCore ? "red.4" : "gray.1"} size="sm" fw={isVirelCore ? 600 : 500}>
                        {user.name}
                      </Text>
                    </Group>
                  <Badge
                    variant="gradient"
                    gradient={{ from: "orange", to: "red" }}
                    size="sm"
                  >
                    {user.balance?.toLocaleString()}
                  </Badge>
                </Group>
              );
              })}
              {leaderboard.length === 0 && (
                <Text c="dimmed" ta="center">
                  No data available
                </Text>
              )}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      <Modal
        opened={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        centered
        size="md"
        radius="lg"
        padding="xl"
        title={
          <Group>
            <ThemeIcon size="lg" radius="md" variant="light" color="green">
              <IconFileSpreadsheet size={20} />
            </ThemeIcon>
            <Text fw={700} size="lg">
              Download Report
            </Text>
          </Group>
        }
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Select the time range and report type for your CSV export. The file
            will download automatically once generated.
          </Text>

          <Paper p="md" withBorder radius="md" bg="var(--bg-surface-2)">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Time Range
              </Text>
              <Select
                data={[
                  { value: "all", label: "All Time" },
                  { value: "7d", label: "Last 7 Days" },
                  { value: "30d", label: "Last 30 Days" },
                  { value: "custom", label: "Custom Range" },
                ]}
                value={timeRange}
                onChange={(v) => setTimeRange(v)}
                allowDeselect={false}
              />
              {timeRange === "custom" && (
                <DatePickerInput
                  type="range"
                  placeholder="Pick dates"
                  value={customDates}
                  onChange={(value) =>
                    setCustomDates(value as [Date | null, Date | null])
                  }
                  maxDate={new Date()}
                  clearable
                />
              )}
            </Stack>
          </Paper>

          <Paper p="md" withBorder radius="md" bg="var(--bg-surface-2)">
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Report Type
              </Text>
              <Select
                data={[
                  { value: "users", label: "Users" },
                  { value: "withdrawals", label: "Withdrawals" },
                  { value: "missions", label: "Missions" },
                  { value: "campaigns", label: "Campaigns" },
                ]}
                value={reportType}
                onChange={(v) => setReportType(v)}
                allowDeselect={false}
              />
            </Stack>
          </Paper>

          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setReportModalOpen(false)}
              disabled={exportLoading}
            >
              Cancel
            </Button>
            <Button
              leftSection={!exportLoading && <IconDownload size={18} />}
              color="green"
              onClick={handleExport}
              loading={exportLoading}
              disabled={!isExportValid}
            >
              Download CSV
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

function StatCard({
  title,
  value,
  diff,
  diffLabel,
  subtitle,
  progress,
  progressColor = "brand.7",
  icon: Icon,
  color,
  trend = "positive",
  onClick,
}: {
  title: string;
  value: string;
  diff?: number;
  diffLabel?: string;
  subtitle?: string;
  progress?: number;
  progressColor?: string;
  icon: any;
  color: string;
  trend?: "positive" | "negative" | "neutral";
  onClick?: () => void;
}) {
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <Group justify="space-between">
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">
          {title}
        </Text>
        <ThemeIcon color={color} variant="light" size="sm">
          <Icon size={16} />
        </ThemeIcon>
      </Group>

      <Group align="flex-end" gap="xs" mt={10}>
        <Text fw={700} size="xl" lh={1}>
          {value}
        </Text>
      </Group>

      {typeof progress !== "undefined" && (
        <Progress
          value={Math.min(100, progress)}
          color={progressColor}
          size="xs"
          radius="xl"
          mt={8}
          aria-label={`${title}: ${Math.round(progress)}%`}
        />
      )}

      {subtitle ? (
        <Text c="dimmed" size="xs" mt={7}>
          {subtitle}
        </Text>
      ) : (
        typeof diff !== "undefined" && (
          <Text c="dimmed" size="xs" mt={7}>
            <Text
              span
              c={
                trend === "positive"
                  ? "teal"
                  : trend === "negative"
                    ? "red"
                    : "blue"
              }
              fw={500}
            >
              {diff > 0 ? "+" : ""}
              {diff}
            </Text>{" "}
            {diffLabel}
          </Text>
        )
      )}
    </Paper>
  );
}

function MenuButton() {
  return (
    <ActionIcon variant="subtle" color="gray">
      <IconDotsVertical size={16} />
    </ActionIcon>
  );
}
